/**
 * useXRMirror — v10.11.0
 * Hook para modo espejo WebRTC. El alumno publica el stream de su cámara
 * (mismo `videoEl` que usa el escáner QR) y el instructor lo consume via
 * peer-to-peer usando el WebSocket de supervisión como signaling relay.
 *
 * Uso (alumno):
 *   const mirror = useXRMirror({ role: 'student', sessionId, accessToken, videoEl });
 *   mirror.viewerCount  // 0 | 1+
 *
 * Uso (instructor):
 *   const mirror = useXRMirror({ role: 'instructor', sessionId, accessToken });
 *   mirror.startViewing()  → mirror.remoteStream (MediaStream)
 *   mirror.stopViewing()
 *
 * Signaling (via WS xr:mirror:*):
 *   instructor → xr:mirror:request  (broadcast to students)
 *   student    → xr:mirror:offer    (SDP offer to instructor)
 *   instructor → xr:mirror:answer   (SDP answer to student)
 *   both       → xr:mirror:ice      (ICE candidates)
 *   both       → xr:mirror:stop
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RTC_CONFIG = {
  iceServers: [{ urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] }],
};

export default function useXRMirror({ role, sessionId, accessToken, videoEl, enabled = true }) {
  const [connected, setConnected] = useState(false);
  const [viewerCount, setViewerCount] = useState(0); // for students
  const [remoteStream, setRemoteStream] = useState(null); // for instructors
  const [status, setStatus] = useState('idle'); // idle | signaling | streaming | error
  const [error, setError] = useState('');

  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const remoteUserRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const closedByUserRef = useRef(false);
  const videoElRef = useRef(videoEl);

  useEffect(() => { videoElRef.current = videoEl; }, [videoEl]);

  const sendWS = useCallback((msg) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  /* ---------- PeerConnection helpers ---------- */
  const closePeer = useCallback(() => {
    if (pcRef.current) {
      try { pcRef.current.close(); } catch { /* noop */ }
      pcRef.current = null;
    }
    setRemoteStream(null);
    setStatus('idle');
    remoteUserRef.current = null;
  }, []);

  const startPeerAsStudent = useCallback(async (instructorId) => {
    try {
      const stream = videoElRef.current?.srcObject;
      if (!stream || !stream.getTracks || stream.getTracks().length === 0) {
        setError('Cámara no activa — inicia el escáner primero');
        return;
      }
      const pc = new RTCPeerConnection(RTC_CONFIG);
      pcRef.current = pc;
      remoteUserRef.current = instructorId;
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      pc.onicecandidate = (ev) => {
        if (ev.candidate) sendWS({ type: 'xr:mirror:ice', target_role: 'instructor', ice: ev.candidate.toJSON() });
      };
      pc.onconnectionstatechange = () => {
        if (['failed', 'closed', 'disconnected'].includes(pc.connectionState)) {
          closePeer();
          setViewerCount(0);
        } else if (pc.connectionState === 'connected') {
          setStatus('streaming');
          setViewerCount(1);
        }
      };
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      sendWS({ type: 'xr:mirror:offer', target_role: 'instructor', sdp: pc.localDescription.toJSON() });
      setStatus('signaling');
    } catch (e) {
      setError(`Offer failed: ${e.message}`);
      closePeer();
    }
  }, [sendWS, closePeer]);

  const handleOfferAsInstructor = useCallback(async (fromUserId, sdp) => {
    try {
      const pc = new RTCPeerConnection(RTC_CONFIG);
      pcRef.current = pc;
      remoteUserRef.current = fromUserId;
      const ms = new MediaStream();
      pc.ontrack = (ev) => {
        ev.streams[0]?.getTracks().forEach((t) => ms.addTrack(t));
        setRemoteStream(ms);
      };
      pc.onicecandidate = (ev) => {
        if (ev.candidate) sendWS({ type: 'xr:mirror:ice', target_role: 'student', ice: ev.candidate.toJSON() });
      };
      pc.onconnectionstatechange = () => {
        if (['failed', 'closed', 'disconnected'].includes(pc.connectionState)) {
          closePeer();
        } else if (pc.connectionState === 'connected') {
          setStatus('streaming');
        }
      };
      await pc.setRemoteDescription(sdp);
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      sendWS({ type: 'xr:mirror:answer', target_role: 'student', sdp: pc.localDescription.toJSON() });
      setStatus('signaling');
    } catch (e) {
      setError(`Answer failed: ${e.message}`);
      closePeer();
    }
  }, [sendWS, closePeer]);

  const handleAnswer = useCallback(async (sdp) => {
    try {
      if (pcRef.current) {
        await pcRef.current.setRemoteDescription(sdp);
      }
    } catch (e) { setError(`setRemoteDescription failed: ${e.message}`); }
  }, []);

  const handleICE = useCallback(async (ice) => {
    try {
      if (pcRef.current && ice) await pcRef.current.addIceCandidate(ice);
    } catch { /* noop, race conditions común */ }
  }, []);

  /* ---------- Signaling WS ---------- */
  const connectWS = useCallback(() => {
    if (!enabled || !sessionId || !accessToken) return;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = (BACKEND_URL || '').replace(/^https?:\/\//, '');
    if (!wsHost) return;
    const mode = role === 'instructor' ? 'instructor' : 'student';
    const url = `${wsProtocol}//${wsHost}/api/ws/simulation/${sessionId}?token=${accessToken}&mode=${mode}&last_seq=0`;
    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onclose = () => {
        setConnected(false);
        wsRef.current = null;
        if (!closedByUserRef.current) {
          clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = setTimeout(connectWS, 3000);
        }
      };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          const et = msg.event_type;
          if (et === 'xr:mirror:request' && role === 'student') {
            // Auto-accept: comienza la oferta
            startPeerAsStudent(msg.from_user_id);
          } else if (et === 'xr:mirror:offer' && role === 'instructor' && msg.sdp) {
            handleOfferAsInstructor(msg.from_user_id, msg.sdp);
          } else if (et === 'xr:mirror:answer' && role === 'student' && msg.sdp) {
            handleAnswer(msg.sdp);
          } else if (et === 'xr:mirror:ice' && msg.ice) {
            handleICE(msg.ice);
          } else if (et === 'xr:mirror:stop') {
            closePeer();
            setViewerCount(0);
          }
        } catch { /* not json */ }
      };
    } catch { /* noop */ }
  }, [enabled, sessionId, accessToken, role, startPeerAsStudent, handleOfferAsInstructor, handleAnswer, handleICE, closePeer]);

  useEffect(() => {
    closedByUserRef.current = false;
    connectWS();
    return () => {
      closedByUserRef.current = true;
      clearTimeout(reconnectTimerRef.current);
      closePeer();
      if (wsRef.current) try { wsRef.current.close(); } catch { /* noop */ }
      wsRef.current = null;
    };
  }, [connectWS, closePeer]);

  /* ---------- API pública ---------- */
  const startViewing = useCallback(() => {
    if (role !== 'instructor') return;
    setError('');
    sendWS({ type: 'xr:mirror:request', target_role: 'student' });
    setStatus('signaling');
  }, [role, sendWS]);

  const stopViewing = useCallback(() => {
    sendWS({ type: 'xr:mirror:stop', target_role: role === 'instructor' ? 'student' : 'instructor' });
    closePeer();
    setViewerCount(0);
  }, [role, sendWS, closePeer]);

  return {
    connected,
    status,
    error,
    remoteStream,
    viewerCount,
    startViewing,
    stopViewing,
  };
}
